import React, { HTMLAttributes, memo, useCallback, useMemo, useState } from "react";
import {
	Box,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Pagination,
	Tooltip,
	Chip,
	Stack,
	Autocomplete,
	TextField,
	Grid,
	Collapse,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Check, Close, KeyboardArrowDown } from "@mui/icons-material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import BlockIcon from '@mui/icons-material/Block';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PersonIcon from '@mui/icons-material/Person';
import ViewOrder from "./ViewOrder";
import GroupsIcon from '@mui/icons-material/Groups';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

type OrderItem = any;

interface Props {
	dataReport: OrderItem[];
	orderResume: any[];
	sellerSelected?: string[] | null;
	rowsPerPage?: number;
	onViewOrder?: (order: OrderItem) => void;
}

const formatDate = (iso?: string) => {
	if (!iso) return "—";
	const d = new Date(iso);
	if (isNaN(d.getTime())) return String(iso);
	return d.toLocaleString();
};

const getTrackingDistance = (trackingDetails: any[] | undefined) => {
	if (!Array.isArray(trackingDetails) || trackingDetails.length === 0) return "—";
	const candidate = trackingDetails[1] ?? trackingDetails[trackingDetails.length - 1] ?? trackingDetails[0];
	const dist = candidate?.distance;
	return typeof dist === "number" ? dist.toFixed(2) : String(dist ?? "—");
};

const OrderRow = memo(({
	order,
	page,
	index,
	onOpen,
}: {
	order: any;
	page: number;
	index: number;
	onOpen: (o: any) => void;
}) => {
	const vendorName = order.vendorName ?? "—";
	const createdAt = order.created_at ?? order.createdAt ?? order.docDate ?? null;
	const numberOrder = order.numberOrder ?? order.number ?? "—";
	const distance = getTrackingDistance(order.trackingDetails);
	const showFlag = order.showFlagDistance === true;
	const status = order.canceled ? "canceled" : order.saveOrderSap ? "saved" : "pending";
	const totalIva = typeof order.totalIva === "number" ? order.totalIva.toLocaleString("es-SV", { minimumFractionDigits: 2 }) : (order.totalIva ?? "—");
	const productsCount = Array.isArray(order.products) ? order.products.length : 0;

	return (
		<TableRow key={order.$key ?? `${page}-${index}`} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
			<TableCell>#{numberOrder}</TableCell>
			<TableCell>
				<Typography variant="body2" fontWeight={600}>{vendorName}</Typography>
				{/* <Typography variant="caption" color="text.secondary">{order.userEmail ?? ""}</Typography> */}
			</TableCell>

			<TableCell>{order.userRouter ?? ''}</TableCell>
			<TableCell>{formatDate(createdAt)}</TableCell>
			<TableCell sx={{ textAlign: "center" }}>{showFlag ?
				<Close sx={{ color: "#ff5c5cff" }} /> :
				<Check sx={{ color: "#5cff64ff" }} />
			}
				<Typography variant="caption" sx={{ fontWeight: 600 }} display="block" color="text.secondary">
					{distance} m
				</Typography>
			</TableCell>
			<TableCell>
				<Typography>{`${order.cardCode ?? "—"}`}</Typography>
			</TableCell>
			<TableCell>
				{/* <Typography variant="body2" fontWeight={600}>{`${order.cardCode ?? "—"}`}</Typography> */}
				<Typography variant="body2" fontWeight={600}>{`${order.cardName ?? order.cardFName ?? "—"}`}</Typography>
				{/* <br /> */}
				<Typography variant="caption" color="text.secondary">{`${order.cardFName ?? "—"}`}</Typography>
			</TableCell>
			<TableCell>
				{status === "saved" && <Typography sx={{ color: "#4caf50", fontWeight: 600 }}>Autorizado</Typography>}
				{status === "pending" && <Typography sx={{ color: "#4dd0e1", fontWeight: 600 }}>Ingresado</Typography>}
				{status === "canceled" && (
					productsCount > 0 ?
						<Typography sx={{ color: "#ff9800", fontWeight: 600 }}>Rechazado</Typography> :
						<Typography sx={{ color: "#f55a4fff", fontWeight: 600 }}>No Efectiva</Typography>
				)}
			</TableCell>
			<TableCell sx={{ textAlign: "center" }}>{productsCount}</TableCell>
			<TableCell sx={{ textAlign: "right", minWidth: 100 }}>$ {totalIva}</TableCell>

			<TableCell align="center">
				<Tooltip title="Ver orden">
					<IconButton size="small" onClick={() => onOpen(order)}>
						<VisibilityIcon />
					</IconButton>
				</Tooltip>
			</TableCell>
		</TableRow>
	);
}, (prevProps, nextProps) => {
	return prevProps.order === nextProps.order && prevProps.page === nextProps.page && prevProps.index === nextProps.index;
});

const SellersOrders = (props: Props) => {
	const { dataReport, orderResume } = props;
	const [page, setPage] = useState<number>(1);
	const rowsPerPage = props.rowsPerPage ?? 15;
	const [openOrder, setOpenOrder] = useState<OrderItem | null>(null);
	const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
	const [selectedClients, setSelectedClients] = useState<string[]>([]);
	const [proximityFilter, setProximityFilter] = useState<'all' | 'yes' | 'no'>('all');
	const [showFilters, setShowFilters] = useState<boolean>(false);

	// Función para obtener el estado de una orden
	const getOrderStatus = (order: any) => {
		const productsCount = Array.isArray(order.products) ? order.products.length : 0;
		if (order.canceled) {
			return productsCount > 0 ? "rejected" : "not-effective";
		}
		return order.saveOrderSap ? "saved" : "pending";
	};

	// Obtener lista única de clientes
	const uniqueClients = useMemo(() => {
		if (!Array.isArray(dataReport)) return [];
		const clientsMap = new Map<string, { cardCode: string; cardName: string; cardFName: string }>();
		dataReport.forEach(order => {
			const cardCode = order.cardCode;
			if (cardCode && !clientsMap.has(cardCode)) {
				clientsMap.set(cardCode, {
					cardCode,
					cardName: order.cardName || '',
					cardFName: order.cardFName || '',
				});
			}
		});
		return Array.from(clientsMap.values()).sort((a, b) => a.cardName.localeCompare(b.cardName));
	}, [dataReport]);

	// Contar órdenes por estado
	const statusCounts = useMemo(() => {
		if (!Array.isArray(dataReport)) return { saved: 0, pending: 0, rejected: 0, notEffective: 0, total: 0 };
		return dataReport.reduce((acc, order) => {
			const status = getOrderStatus(order);
			if (status === "saved") acc.saved++;
			else if (status === "pending") acc.pending++;
			else if (status === "rejected") acc.rejected++;
			else if (status === "not-effective") acc.notEffective++;
			acc.total++;
			return acc;
		}, { saved: 0, pending: 0, rejected: 0, notEffective: 0, total: 0 });
	}, [dataReport]);

	// Calcular estadísticas de clientes desde orderResume
	const clientStats = useMemo(() => {
		if (!Array.isArray(orderResume) || orderResume.length === 0) {
			return { visitedClients: 0, pendingClients: 0, totalClients: 0 };
		}

		// Sumar los valores de cada vendedor
		const totals = orderResume.reduce((acc, seller) => {
			// allsClients: total de clientes únicos del vendedor
			// visitClient: clientes visitados del vendedor
			acc.totalClients += seller.allsClients || 0;
			acc.visitedClients += seller.visitClient || 0;
			return acc;
		}, { totalClients: 0, visitedClients: 0 });

		// Los clientes pendientes son la diferencia entre total y visitados
		const pendingClients = totals.totalClients - totals.visitedClients;

		return {
			visitedClients: totals.visitedClients,
			pendingClients: pendingClients,
			totalClients: totals.totalClients
		};
	}, [orderResume]);

	// Filtrar datos por estados seleccionados y clientes
	const filteredData = useMemo(() => {
		if (!Array.isArray(dataReport)) return [];
		let result = dataReport;

		// Filtrar por estado
		if (activeFilters.size > 0) {
			result = result.filter(order => activeFilters.has(getOrderStatus(order)));
		}

		// Filtrar por clientes
		if (selectedClients.length > 0) {
			result = result.filter(order => selectedClients.includes(order.cardCode));
		}

		// Filtrar por proximidad (cliente visitado)
		if (proximityFilter !== 'all') {
			result = result.filter(order => {
				const isVisited = !order.showFlagDistance; // true = visitado (check verde)
				return proximityFilter === 'yes' ? isVisited : !isVisited;
			});
		}

		return result;
	}, [dataReport, activeFilters, selectedClients, proximityFilter]);

	const pagedData = useMemo(() => {
		const start = (page - 1) * rowsPerPage;
		return filteredData.slice(start, start + rowsPerPage);
	}, [filteredData, page, rowsPerPage]);

	const pageCount = useMemo(() => {
		if (filteredData.length === 0) return 1;
		return Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
	}, [filteredData, rowsPerPage]);

	// Manejar toggle de filtros
	const handleFilterToggle = (status: string) => {
		setActiveFilters(prev => {
			const newFilters = new Set(prev);
			if (newFilters.has(status)) {
				newFilters.delete(status);
			} else {
				newFilters.add(status);
			}
			return newFilters;
		});
		setPage(1);
	};

	// Limpiar todos los filtros
	const handleClearFilters = () => {
		setActiveFilters(new Set());
		setSelectedClients([]);
		setProximityFilter('all');
		setPage(1);
	};

	// Manejar cambio en selección de clientes
	const handleClientsChange = (_event: any, newValue: typeof uniqueClients) => {
		setSelectedClients(newValue.map(client => client.cardCode));
		setPage(1);
	};

	// Manejar cambio en filtro de proximidad
	const handleProximityFilterChange = (value: 'all' | 'yes' | 'no') => {
		setProximityFilter(value);
		setPage(1);
	};

	const handleOpen = useCallback((order: OrderItem) => {
		setOpenOrder(order);
		if (props.onViewOrder) props.onViewOrder(order);
	}, [props]);

	const renderItem = useMemo(() => (props: HTMLAttributes<HTMLLIElement> & { key: any; }, option: { cardCode: string; cardName: string; cardFName: string; }) => {
		return (
			<Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
				<Box sx={{
					backgroundColor: '#e3f2fd',
					borderRadius: '50%',
					p: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					minWidth: 40,
					minHeight: 40
				}}>
					<PersonIcon sx={{ color: '#1976d2', fontSize: 20 }} />
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
					<Typography variant="body2" fontWeight={600} sx={{ color: '#183555' }}>
						{option.cardCode} - {option.cardName}
					</Typography>
					<Typography variant="caption" sx={{ color: '#666', mt: 0.5 }}>
						{option.cardFName}
					</Typography>
				</Box>
			</Box>
		);
	}, [])

	return (
		<>
			<Grid container spacing={3} mb={2} px={2} pt={3}>
				<Grid size={4}>
					<Paper
						elevation={3}
						sx={{
							padding: 3,
							mb: 2,
							background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							borderRadius: 2,
							transition: 'transform 0.2s',
							'&:hover': {
								transform: 'translateY(-4px)',
								boxShadow: '0 8px 16px rgba(76, 175, 80, 0.3)'
							}
						}}
					>
						<Box>
							<Typography variant="h3" fontWeight="bold" sx={{ color: "#ffffff", mb: 0.5 }}>
								{clientStats.visitedClients}
							</Typography>
							<Typography variant="subtitle1" fontWeight="600" sx={{ color: "#ffffff" }}>
								Clientes visitados
							</Typography>
						</Box>
						<Box sx={{
							backgroundColor: 'rgba(255, 255, 255, 0.2)',
							borderRadius: '50%',
							p: 2,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}>
							<CheckCircleIcon sx={{ fontSize: 40, color: '#ffffff' }} />
						</Box>
					</Paper>
				</Grid>
				<Grid size={4}>
					<Paper
						elevation={3}
						sx={{
							padding: 3,
							mb: 2,
							background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							borderRadius: 2,
							transition: 'transform 0.2s',
							'&:hover': {
								transform: 'translateY(-4px)',
								boxShadow: '0 8px 16px rgba(255, 152, 0, 0.3)'
							}
						}}
					>
						<Box>
							<Typography variant="h3" fontWeight="bold" sx={{ color: "#ffffff", mb: 0.5 }}>
								{clientStats.pendingClients}
							</Typography>
							<Typography variant="subtitle1" fontWeight="600" sx={{ color: "#ffffff" }}>
								Clientes pendientes
							</Typography>
						</Box>
						<Box sx={{
							backgroundColor: 'rgba(255, 255, 255, 0.2)',
							borderRadius: '50%',
							p: 2,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}>
							<PendingActionsIcon sx={{ fontSize: 40, color: '#ffffff' }} />
						</Box>
					</Paper>
				</Grid>
				<Grid size={4}>
					<Paper
						elevation={3}
						sx={{
							padding: 3,
							mb: 2,
							background: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							borderRadius: 2,
							transition: 'transform 0.2s',
							'&:hover': {
								transform: 'translateY(-4px)',
								boxShadow: '0 8px 16px rgba(33, 150, 243, 0.3)'
							}
						}}
					>
						<Box>
							<Typography variant="h3" fontWeight="bold" sx={{ color: "#ffffff", mb: 0.5 }}>
								{clientStats.totalClients}
							</Typography>
							<Typography variant="subtitle1" fontWeight="600" sx={{ color: "#ffffff" }}>
								Total clientes
							</Typography>
						</Box>
						<Box sx={{
							backgroundColor: 'rgba(255, 255, 255, 0.2)',
							borderRadius: '50%',
							p: 2,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}>
							<GroupsIcon sx={{ fontSize: 40, color: '#ffffff' }} />
						</Box>
					</Paper>
				</Grid>
			</Grid>
			<Box sx={{ width: "100%", p: 2 }}>
				{/* Sección de filtros */}
				<Paper elevation={2} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<FilterAltIcon sx={{ mr: 1, color: '#183555' }} />
							<Typography variant="subtitle1" fontWeight={600} sx={{ color: '#183555' }}>
								Filtros
							</Typography>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<Typography variant="body2" sx={{ color: '#666' }}>
								{(activeFilters.size > 0 || selectedClients.length > 0 || proximityFilter !== 'all')
									? `${filteredData.length} de ${statusCounts.total}`
									: `${statusCounts.total}`} órdenes
							</Typography>
							<IconButton onClick={() => setShowFilters(prev => !prev)}>
								<KeyboardArrowDown sx={{ transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
							</IconButton>
						</Box>
					</Box>

					<Collapse in={showFilters} mountOnEnter unmountOnExit>
						<Box sx={{ borderTop: '1px solid #ddd', pt: 2 }}>
							{/* Filtro por cliente */}
							<Box sx={{ mt: 2 }}>
								<Typography variant="body2" fontWeight={600} sx={{ color: '#183555', mb: 1 }}>
									Por cliente:
								</Typography>
								<Autocomplete
									multiple
									options={uniqueClients}
									getOptionLabel={(option) => `${option.cardCode} - ${option.cardName} - ${option.cardFName}`}
									value={uniqueClients.filter(client => selectedClients.includes(client.cardCode))}
									onChange={handleClientsChange}
									isOptionEqualToValue={(option, value) => option.cardCode === value.cardCode}
									renderOption={renderItem}
									renderInput={(params) => (
										<TextField
											{...params}
											variant="outlined"
											placeholder="Selecciona uno o varios clientes..."
											size="small"
										/>
									)}
									renderTags={(value, getTagProps) =>
										value.map((option, index) => (
											<Chip
												icon={<PersonIcon />}
												label={`${option.cardCode} - ${option.cardName} - ${option.cardFName}`}
												size="small"
												color="primary"
												{...getTagProps({ index })}
											/>
										))
									}
									sx={{
										'& .MuiOutlinedInput-root': {
											backgroundColor: '#ffffff',
										}
									}}
								/>
							</Box>

							<Grid container spacing={2} sx={{ mt: 2 }}>
								<Grid size={8}>
									{/* Filtro por estado */}
									<Typography variant="body2" fontWeight={600} sx={{ color: '#183555', mb: 1, mt: 2 }}>
										Por estado:
									</Typography>
									<Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
										<Chip
											icon={<CheckCircleIcon />}
											label={`Autorizados (${statusCounts.saved})`}
											onClick={() => handleFilterToggle("saved")}
											color="success"
											variant={activeFilters.has("saved") ? "filled" : "outlined"}
											sx={{
												fontWeight: 600,
												fontSize: '0.875rem',
												transition: 'all 0.2s',
												cursor: 'pointer',
												'&:hover': {
													transform: 'translateY(-2px)',
													boxShadow: 2,
												}
											}}
										/>
										<Chip
											icon={<HourglassEmptyIcon style={{ color: activeFilters.has("pending") ? "#FFF" : "#4dd0e1" }} />}
											label={`Ingresados (${statusCounts.pending})`}
											onClick={() => handleFilterToggle("pending")}
											variant={activeFilters.has("pending") ? "filled" : "outlined"}
											sx={{
												fontWeight: 600,
												fontSize: '0.875rem',
												transition: 'all 0.2s',
												cursor: 'pointer',
												...(activeFilters.has("pending") ? {
													backgroundColor: '#4dd0e1',
													color: '#fff',
													'&:hover': {
														backgroundColor: '#26c6da',
														transform: 'translateY(-2px)',
														boxShadow: 2,
													}
												} : {
													borderColor: '#4dd0e1',
													color: '#4dd0e1',
													'&:hover': {
														backgroundColor: 'rgba(77, 208, 225, 0.08)',
														transform: 'translateY(-2px)',
														boxShadow: 2,
													}
												})
											}}
										/>
										<Chip
											icon={<CancelIcon />}
											label={`Rechazados (${statusCounts.rejected})`}
											onClick={() => handleFilterToggle("rejected")}
											color="warning"
											variant={activeFilters.has("rejected") ? "filled" : "outlined"}
											sx={{
												fontWeight: 600,
												fontSize: '0.875rem',
												transition: 'all 0.2s',
												cursor: 'pointer',
												'&:hover': {
													transform: 'translateY(-2px)',
													boxShadow: 2,
												}
											}}
										/>
										<Chip
											icon={<BlockIcon />}
											label={`No Efectivas (${statusCounts.notEffective})`}
											onClick={() => handleFilterToggle("not-effective")}
											color="error"
											variant={activeFilters.has("not-effective") ? "filled" : "outlined"}
											sx={{
												fontWeight: 600,
												fontSize: '0.875rem',
												transition: 'all 0.2s',
												cursor: 'pointer',
												'&:hover': {
													transform: 'translateY(-2px)',
													boxShadow: 2,
												}
											}}
										/>
									</Stack>

								</Grid>
								<Grid size={4}>

									{/* Filtro por proximidad (Cliente visitado) */}
									<Box sx={{ mt: 2 }}>
										<Typography variant="body2" fontWeight={600} sx={{ color: '#183555', mb: 1 }}>
											Cliente visitado:
										</Typography>
										<Stack direction="row" spacing={1.5}>
											<Chip
												icon={<Check />}
												label="Sí"
												onClick={() => handleProximityFilterChange(proximityFilter === 'yes' ? 'all' : 'yes')}
												color="success"
												variant={proximityFilter === 'yes' ? "filled" : "outlined"}
												sx={{
													fontWeight: 600,
													fontSize: '0.875rem',
													transition: 'all 0.2s',
													cursor: 'pointer',
													'&:hover': {
														transform: 'translateY(-2px)',
														boxShadow: 2,
													}
												}}
											/>
											<Chip
												icon={<Close />}
												label="No"
												onClick={() => handleProximityFilterChange(proximityFilter === 'no' ? 'all' : 'no')}
												color="error"
												variant={proximityFilter === 'no' ? "filled" : "outlined"}
												sx={{
													fontWeight: 600,
													fontSize: '0.875rem',
													transition: 'all 0.2s',
													cursor: 'pointer',
													'&:hover': {
														transform: 'translateY(-2px)',
														boxShadow: 2,
													}
												}}
											/>
										</Stack>
									</Box>
								</Grid>
							</Grid>
						</Box>
					</Collapse>


					{/* Botón limpiar todos los filtros */}
					{(activeFilters.size > 0 || selectedClients.length > 0 || proximityFilter !== 'all') && (
						<Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
							<Chip
								label="Limpiar todos los filtros"
								onClick={handleClearFilters}
								onDelete={handleClearFilters}
								color="primary"
								variant="filled"
								sx={{
									fontWeight: 600,
									fontSize: '0.875rem',
								}}
							/>
						</Box>
					)}
				</Paper>

				<Paper elevation={2}>
					<TableContainer sx={{ minHeight: 40, maxHeight: !showFilters ? "44vh" : "auto" }}>
						<Table stickyHeader aria-label="Reporte de órdenes">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 700 }}>N°</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Vendedor</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Cod. Vendedor</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Proximidad (m)</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Cod. Cliente</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
									<TableCell sx={{ fontWeight: 700 }}># Prod.</TableCell>
									<TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
									<TableCell sx={{ fontWeight: 700, textAlign: "center" }}>Acciones</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{pagedData.map((order, idx) => (
									<OrderRow key={order.$key ?? `${page}-${idx}`} order={order} page={page} index={idx} onOpen={handleOpen} />
								))}

								{filteredData.length === 0 && (
									<TableRow>
										<TableCell colSpan={11} sx={{ py: 6, textAlign: "center" }}>
											<Typography color="text.secondary">
												{activeFilters.size > 0
													? "No hay órdenes que coincidan con los filtros seleccionados."
													: "No hay registros para mostrar."}
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
						<Pagination
							count={pageCount}
							page={page}
							onChange={(_, val) => setPage(val)}
							color="primary"
							shape="rounded"
						/>
					</Box>
				</Paper>
				<ViewOrder order={openOrder} openOrder={Boolean(openOrder)} setOpenOrder={setOpenOrder} />
			</Box>
		</>
	);
};

export default React.memo(SellersOrders);